enum ScriptStatus {
    LOADING = "loading",
    LOADED = "loaded",
    ERROR = "error"
}

const LOG_LEVELS = new Map([
    ["debug", 0],
    ["info", 1],
    ["warn", 2],
    ["error", 3]
]);

class ScriptController {
    public name = "scriptController";
    private scripts: Map<string, ScriptStatus> = new Map();
    private listeners: Map<string, Array<(lib: any) => void>> = new Map();
    private events: Record<string, Record<string, Array<(data: any) => void>>> = {};
    private secureDOM: Document | null = null;
    private cachedScripts: Set<string> = new Set();
    public storeInWindow: boolean = true;
    public useSecureDOM: boolean = false;
    public enableLogging: boolean = true;
    public logLevel: "debug" | "info" | "warn" | "error" = "warn";

    constructor() {
        this.log("ScriptController initialisiert", "info");
    }

    private log(message: string, level: ("debug" | "info" | "warn" | "error")): void {
        if (!this.enableLogging) return;
        const currentLevel = LOG_LEVELS.get(level);
        const configuredLevel = LOG_LEVELS.get(this.logLevel);
        if (currentLevel !== undefined && configuredLevel !== undefined && currentLevel >= configuredLevel) {
            console[level](message);
        }
    }

    private getSecureDOM(): Document {
        if (!this.secureDOM) {
            this.log("Secure DOM wird erstellt", "debug");
            this.secureDOM = document.implementation.createHTMLDocument("Secure DOM");
        }
        return this.secureDOM;
    }

    public triggerEvent(url: string, eventType: string, data: any = null): void {
        if (this.events[url]?.[eventType]) {
            this.events[url][eventType].forEach(cb => cb(data));
        }
    }

    public onEvent(url: string, eventType: string, callback: (data: any) => void): void {
        if (!this.events[url]) this.events[url] = {};
        if (!this.events[url][eventType]) this.events[url][eventType] = [];
        this.events[url][eventType].push(callback);
    }

    public getAllScripts(): string[] {
        return Array.from(this.scripts.keys());
    }

    public getScriptStatus(url: string): ScriptStatus | "not_loaded" {
        return this.scripts.has(url) ? this.scripts.get(url)! : "not_loaded";
    }

    public reloadScript(url: string, options: { globalVar?: string; attributes?: Record<string, string>; isModule?: boolean; useSecureDOM?: boolean; } = {}): Promise<any> {
        this.removeScript(url);
        return this.loadScript(url, options);
    }

    public removeScript(url: string, globalVar?: string): void {
        document.querySelectorAll(`script[src^="${url}"]`).forEach(script => script.remove());

        this.scripts.delete(url);
        this.cachedScripts.delete(url);
        this.listeners.delete(url);

        if (globalVar) {
            if ((window as any)[globalVar]) {
                delete (window as any)[globalVar];
                this.log(`Globales Objekt entfernt: ${globalVar}`, "debug");
            }
        }

        this.triggerEvent(url, "onRemove");
        this.log(`Skript entfernt: ${url}`, "debug");
    }

    public useScript(
        url: string,
        options: {
            globalVar?: string;
            attributes?: Record<string, string>;
            isModule?: boolean;
            useSecureDOM?: boolean;
        } = {}
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            const existingStatus = this.scripts.get(url);

            // ✅ Schon geladen – direkt auflösen
            if (existingStatus === ScriptStatus.LOADED || this.cachedScripts.has(url)) {
                this.log(`Skript bereits geladen: ${url}`, "info");
                this.triggerEvent(url, "onCached");

                return resolve(options.globalVar ? (window as any)[options.globalVar] : null);
            }

            // ⏳ Wird gerade geladen – callback einreihen
            if (existingStatus === ScriptStatus.LOADING) {
                this.log(`Skript wird bereits geladen, Callback angehängt: ${url}`, "debug");
                this.listeners.get(url)?.push(resolve);
                return;
            }

            // 🚀 Neues Skript – jetzt als "LOADING" markieren, bevor geladen wird
            this.scripts.set(url, ScriptStatus.LOADING);
            this.listeners.set(url, [resolve]);

            this.loadScript(url, options)
                .then(lib => {
                    this.scripts.set(url, ScriptStatus.LOADED);
                    this.cachedScripts.add(url);

                    // 🔁 Alle Listener ausführen
                    const listeners = this.listeners.get(url) || [];
                    listeners.forEach(fn => {
                        try {
                            fn(options.globalVar ? (window as any)[options.globalVar] : lib);
                        } catch (err) {
                            this.log(`Fehler beim Ausführen eines Listeners für ${url}`, "error");
                        }
                    });

                    this.listeners.delete(url);
                })
                .catch(error => {
                    this.scripts.delete(url);
                    this.listeners.delete(url);
                    this.log(`Fehler beim Laden von ${url}`, "error");
                    reject(error);
                });
        });
    }

    public loadScript(
        url: string,
        options: {
            globalVar?: string;
            attributes?: Record<string, string>;
            isModule?: boolean;
            useSecureDOM?: boolean;
        } = {}
    ): Promise<any> {
        this.log(`🔄 Lade Skript: ${url}`, "info");

        return new Promise((resolve, reject) => {
            // Frühzeitiger Exit, wenn schon geladen
            const existingStatus = this.scripts.get(url);
            if (existingStatus === ScriptStatus.LOADED || this.cachedScripts.has(url)) {
                this.log(`⚠️ Skript bereits vollständig geladen: ${url}`, "info");
                this.triggerEvent(url, "onLoad");

                return resolve(options.globalVar ? (window as any)[options.globalVar] : null);
            }

            // Listener vorbereiten
            if (!this.listeners.has(url)) {
                this.listeners.set(url, []);
            }
            this.listeners.get(url)?.push(resolve);

            // Status setzen
            this.scripts.set(url, ScriptStatus.LOADING);
            this.triggerEvent(url, "onStartLoading");

            const useSecure = options.useSecureDOM ?? this.useSecureDOM;
            const root = useSecure ? this.getSecureDOM() : document;

            // Duplikatschutz: Script bereits im DOM?
            if (root.querySelector(`script[src="${url}"]`)) {
                this.log(`⚠️ Skript bereits im DOM vorhanden: ${url}`, "warn");
                this.scripts.set(url, ScriptStatus.LOADED);
                this.cachedScripts.add(url);
                this.triggerEvent(url, "onLoad");

                const libRef = options.globalVar ? (window as any)[options.globalVar] : null;
                this.listeners.get(url)?.forEach(cb => cb(libRef));
                this.listeners.delete(url);

                return resolve(libRef); // <- WICHTIG: Promise wird erfüllt
            }

            const script = root.createElement("script");
            script.src = url;
            script.async = true;
            if (options.isModule) script.type = "module";

            // Attribute setzen
            for (const [key, value] of Object.entries(options.attributes || {})) {
                if (value != null) script.setAttribute(key, value);
            }

            // Erfolg
            script.onload = () => {
                this.log(`✅ Skript geladen: ${url}`, "info");
                this.scripts.set(url, ScriptStatus.LOADED);
                this.cachedScripts.add(url);

                this.triggerEvent(url, "onLoad");

                const libRef = options.globalVar ? (window as any)[options.globalVar] : null;

                this.listeners.get(url)?.forEach(cb => cb(libRef));
                this.listeners.delete(url);
            };

            // Fehler
            script.onerror = (event) => {
                this.log(`❌ Fehler beim Laden: ${url}`, "error");
                this.triggerEvent(url, "onError", event);

                this.scripts.set(url, ScriptStatus.ERROR);

                this.listeners.get(url)?.forEach(cb => cb(null));
                this.listeners.delete(url);
                reject(new Error(`Fehler beim Laden des Skripts: ${url}`));
            };

            // Einfügen ins DOM
            (useSecure ? root.head : document.head).appendChild(script);
        });
    }
}

const scriptController = new ScriptController();

export default scriptController;
